import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DatabaseZap, Grid3x3, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '@clarix/ui';

interface ModuleUnavailableProps {
  /** Display name of the app, e.g. "Projects". */
  appName: string;
  /** The AppModule code the tenant is not entitled to, e.g. 'timesheet'. */
  moduleCode: string;
  /**
   * Exact module name from the registry (`workspace.AppModule.name`), when the
   * caller has it. Falls back to a humanised `moduleCode` — today `/me` carries
   * only the codes, so this is the seam for when it starts carrying names.
   */
  moduleName?: string;
  /** Where "Back" goes — the app's own home. */
  appHref: string;
}

/** 'timesheet' → 'Timesheet', 'platform_health' → 'Platform Health'. */
function humanize(code: string): string {
  return code
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * This workspace's plan does not include the module the user just navigated to.
 *
 * Deliberately NOT the outage panel and NOT a permission error:
 *   - an outage says "try again" — here retrying will never work;
 *   - a permission error says "ask your admin for access" — here the admin
 *     cannot grant it either, because the module is not in the tenant's plan at
 *     all. Only a Sarvadhi platform admin can change that.
 * Sending the user down either of those paths wastes their time and generates a
 * support ticket for a question neither the user nor their admin can answer.
 *
 * So the three facts below are the whole design: this is a plan boundary and
 * not a bug, nothing was deleted, and the person to ask is outside the
 * workspace. They are spelled out as separate rows rather than buried in a
 * paragraph, because a user who reads only one line must still not file the
 * ticket.
 *
 * There is deliberately no retry button and no auto-recovery poll — nothing to
 * recover from. The rail item and settings tab are already hidden, so the only
 * ways to land here are a typed URL, a bookmark, or a pasted deep link, which
 * is exactly the case that deserves an explanation rather than a silent bounce
 * back to home.
 *
 * Renders INSIDE the shell chrome so the rail and app switcher stay usable —
 * same containment as <AppUnavailable>, but visibly calmer: nothing is broken,
 * so nothing is amber.
 */
export function ModuleUnavailable({
  appName,
  moduleCode,
  moduleName,
  appHref,
}: ModuleUnavailableProps) {
  const name = moduleName ?? humanize(moduleCode);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="flex flex-col items-center text-center">
          <div className="bg-muted ring-border mb-5 flex size-14 items-center justify-center rounded-full ring-1">
            <Lock className="text-muted-foreground size-6" strokeWidth={1.5} aria-hidden />
          </div>

          <h1 className="text-foreground text-4xl font-semibold tracking-tight">
            {name} isn&apos;t part of your plan
          </h1>

          <p className="text-muted-foreground mt-2 text-xl leading-relaxed">
            {appName} is available to this workspace, but the {name} module isn&apos;t included.
            That&apos;s a plan boundary, not a problem with the page.
          </p>
        </div>

        <div className="border-border bg-surface divide-border/60 mt-6 divide-y rounded-xl border">
          <Fact icon={DatabaseZap} title="Nothing has been deleted">
            Everything already recorded in {name} is untouched, and reappears exactly as it was if
            the module is switched back on.
          </Fact>
          <Fact icon={ShieldCheck} title="This isn't a permission">
            A workspace admin can&apos;t grant access to it — it sits above roles and permissions
            entirely.
          </Fact>
          <Fact icon={Grid3x3} title="To add it, talk to your Clarix account manager">
            Only Clarix can change which modules a workspace includes.
          </Fact>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <Button asChild size="sm" className="px-3">
            <Link to={appHref}>Back to {appName}</Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="gap-1.5 px-3">
            <Link to="/apps">
              <Grid3x3 className="size-3.5" strokeWidth={1.5} aria-hidden />
              All apps
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** One of the three things the user must leave with. */
function Fact({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 p-3.5">
      <Icon className="text-muted-foreground mt-0.5 size-4 shrink-0" strokeWidth={1.5} aria-hidden />
      <div className="min-w-0">
        <p className="text-foreground text-lg font-medium">{title}</p>
        <p className="text-muted-foreground mt-0.5 text-lg leading-relaxed">{children}</p>
      </div>
    </div>
  );
}
